import './useMFAAuthenticationHookConfig';
import { useTranslation } from 'react-i18next';

import {
  IResendMFACodeByUserIdAndSentTypeParams,
  IResendMFACodeByUserIdAndSentTypeResponse,
  useResendMFACodeByUserIdAndSentType,
} from '../../operations/mutations/mfa/useResendMFACodeByUserIdAndSentType/useResendMFACodeByUserIdAndSentType';
import {
  ISetMFAAuthenticationByUserIdParams,
  ISetMFAAuthenticationByUserIdResponse,
  useSetMFAAuthenticationByUserId,
} from '../../operations/mutations/mfa/useSetMFAAuthenticationByUserId/useSetMFAAuthenticationByUserId';
import {
  IValidateMFACodeParams,
  IValidateMFACodeResponse,
  useLazyValidateMFACode,
} from '../../operations/query/mfa/useValidateMFACode/useValidateMFACode';
import useNotificationsHook from '../notificationsHook/useNotificationsHook';

export interface IMFAAuthenticationHook {
  operations: {
    handleValidateMFACode: (params: IValidateMFACodeParams) => Promise<IValidateMFACodeResponse | undefined>;
    validateMFACodeData: IValidateMFACodeResponse | undefined;
    loadingValidateMFACode: boolean;
    handleSetMFAAuthentication: (
      params: ISetMFAAuthenticationByUserIdParams,
    ) => Promise<ISetMFAAuthenticationByUserIdResponse | undefined>;
    handleResendMFACode: (
      params: IResendMFACodeByUserIdAndSentTypeParams,
    ) => Promise<IResendMFACodeByUserIdAndSentTypeResponse | undefined>;
  };
}

const useMFAAuthenticationHook = (): IMFAAuthenticationHook => {
  const { t } = useTranslation('useMFAAuthenticationHook');
  const [getValidateMFACode, { data: validateMFACodeData, loading: loadingValidateMFACode }] = useLazyValidateMFACode();
  const [setMFAAuthentication] = useSetMFAAuthenticationByUserId();
  const [resendMFACode] = useResendMFACodeByUserIdAndSentType();
  const { operations: notificationOps } = useNotificationsHook();

  const handleValidateMFACode = (params: IValidateMFACodeParams): Promise<IValidateMFACodeResponse | undefined> =>
    getValidateMFACode({ variables: params })
      .then((response) => {
        if (response.data?.validateMFACode.success === true) {
          notificationOps.addNotification('success', t('VALIDATE_DESCRIPTION_TEXT'), t('VALIDATE_TITLE_TEXT'));
        }

        return response.data ?? undefined;
      })
      .catch(() => {
        notificationOps.addNotification('danger', t('ERROR_DESCRIPTION_TEXT'), t('ERROR_TITLE_TEXT'));

        return undefined;
      });

  const handleSetMFAAuthentication = (
    params: ISetMFAAuthenticationByUserIdParams,
  ): Promise<ISetMFAAuthenticationByUserIdResponse | undefined> =>
    setMFAAuthentication({ variables: params })
      .then((response) => {
        if (response.data?.setMFAAuthenticationByUserId.success === true) {
          notificationOps.addNotification('emailSend', t('SET_DESCRIPTION_TEXT'), t('SET_TITLE_TEXT'));
        }

        return response.data ?? undefined;
      })
      .catch(() => {
        notificationOps.addNotification('danger', t('ERROR_DESCRIPTION_TEXT'), t('ERROR_TITLE_TEXT'));

        return undefined;
      });

  const handleResendMFACode = (
    params: IResendMFACodeByUserIdAndSentTypeParams,
  ): Promise<IResendMFACodeByUserIdAndSentTypeResponse | undefined> =>
    resendMFACode({ variables: params })
      .then((response) => {
        if (response.data?.resendMFACodeByUserIdAndSentType.success === true) {
          notificationOps.addNotification('emailResend', t('RESEND_DESCRIPTION_TEXT'), t('RESEND_TITLE_TEXT'));
        }

        return response.data ?? undefined;
      })
      .catch(() => {
        notificationOps.addNotification('danger', t('ERROR_DESCRIPTION_TEXT'), t('ERROR_TITLE_TEXT'));

        return undefined;
      });

  return {
    operations: {
      handleValidateMFACode,
      validateMFACodeData,
      loadingValidateMFACode,
      handleSetMFAAuthentication,
      handleResendMFACode,
    },
  };
};

export default useMFAAuthenticationHook;
